/** @jsx jsx */
import { jsx } from '@emotion/core';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { Grid, useTheme, useMediaQuery } from '@material-ui/core';

import { useImgSrc } from 'core/utils/use-img-src.hook';
import { currencySelector } from 'reducers/app.reducer';
import {
  ICustomTheme,
  ChallengesType,
  ChallengePhaseType,
  ChallengeGoalTypes,
  ChallengeResultType,
  LoadingStatusType,
} from 'interfaces';
import {
  LoadingError,
  ChallengeDescriptionSkeleton,
  ParticipateDetailsButtons,
  ChallengeDeclareButton,
} from 'components';
import {
  getChallengeDescriptionTitle,
  sumGoalRewards,
} from 'core/utils/challenges.helpers';
import ActionParticipation from 'components/widgets/WidgetAction/ActionParticipation';
import CustomSponsorIcon from 'assets/icons/CustomSponsorIcon';
import { getSafeHTML } from 'utils';
import { LOADING_STATUS } from 'constants/status';
import { GOAL, CHALLENGE } from 'constants/challenges';
import { getIsDisabledDeal } from '../utils';
import * as S from './styles';

const { cssContainerCenteredButtons, cssButtonsContainer } = S;

type ChallengeDescriptionTypes = {
  challenge: ChallengesType;
  challengeResults?: Partial<ChallengeResultType>;
  challengeStatus: LoadingStatusType;
  currentPhase?: ChallengePhaseType | null;
  isParticipating?: boolean;
  personalChallengeGoals?: ChallengeGoalTypes;
};

const ChallengeDescription = ({
  challenge,
  challengeResults,
  challengeStatus,
  currentPhase,
  isParticipating,
  personalChallengeGoals,
}: ChallengeDescriptionTypes): JSX.Element | null => {
  const {
    t,
    i18n: { language },
  } = useTranslation();
  const theme: ICustomTheme = useTheme();
  const isDownSm = useMediaQuery(theme.breakpoints.down('sm'));
  const isDownMd = useMediaQuery(theme.breakpoints.down('md'));
  const currency = useSelector(currencySelector);
  const {
    challengeGoals,
    dealCategory,
    dealImage,
    goalType,
    inscriptionsLeft,
    isDealCategoryVisible,
    resultLimit,
    sponsor,
    teaser,
    title,
    type,
  } = challenge || {};
  const { fullUri: sponsorImg } = useImgSrc(dealImage);

  const isDisabledDeal = getIsDisabledDeal(type, isParticipating, currentPhase);

  let formattedGoals: ChallengeGoalTypes[] = [];
  if (type !== CHALLENGE.LIST) {
    switch (goalType) {
      case GOAL.CUSTOM:
        if (personalChallengeGoals) {
          formattedGoals = [
            {
              ...personalChallengeGoals,
              value: challengeResults?.customResultValue,
            },
          ];
        }
        break;
      case GOAL.BY_PROFILE:
        if (challengeResults) {
          formattedGoals = [
            {
              '@id': challengeResults['@id']!,
              '@type': challengeResults['@type']!,
              name: challengeResults.customResultName || '-',
              subject: challengeResults.customResultSubject || '-',
              value: challengeResults.customResultValue,
              unit: challengeResults.customResultUnit || '-',
            },
          ];
        }
        break;
      default:
        formattedGoals = challengeGoals;
    }
  }

  const showDealCategory =
    type === CHALLENGE.DEAL &&
    isDealCategoryVisible &&
    dealCategory &&
    dealCategory.length > 0;
  const rewardsMax = formattedGoals ? sumGoalRewards(formattedGoals) : null;

  const getContent = (): JSX.Element => {
    const safeTeaser = getSafeHTML(teaser || '');
    switch (challengeStatus) {
      case LOADING_STATUS.PENDING:
        return <ChallengeDescriptionSkeleton />;
      case LOADING_STATUS.REJECTED:
        return <LoadingError />;
      default:
        return (
          <Grid
            container
            item
            direction="column"
            alignItems="center"
            className={isDisabledDeal ? 'disabled' : ''}
          >
            <S.Title variant={isDownSm ? 'h5' : 'h3'}>{title}</S.Title>
            {showDealCategory && (
              <S.Category variant={isDownSm ? 'h6' : 'h4'}>
                {dealCategory}
              </S.Category>
            )}

            <Grid
              container
              item
              spacing={isDownMd ? 2 : 8}
              direction={isDownMd ? 'column' : 'row'}
            >
              {type === CHALLENGE.DEAL && sponsorImg && (
                <Grid
                  container
                  item
                  justify="center"
                  md={12}
                  lg={5}
                  css={{ display: 'flex', alignContent: 'center' }}
                >
                  <S.SponsorImage src={sponsorImg} alt="deal illustration" />
                </Grid>
              )}

              <Grid
                container
                item
                md={12}
                lg={type === CHALLENGE.CLASSIC ? 12 : true}
                direction="column"
                justify="center"
                alignItems={isDownMd || !sponsorImg ? 'center' : 'flex-start'}
              >
                {rewardsMax && (
                  <S.WinUpTo variant={isDownSm ? 'h6' : 'h4'}>
                    <span css={{ textTransform: 'uppercase', fontWeight: 200 }}>
                      {t('challenges.max_to_win')}
                    </span>
                    &nbsp;
                    <strong css={{ fontWeight: 900, fontSize: '110%' }}>
                      {rewardsMax.toLocaleString()}{' '}
                      <span css={{ textTransform: 'lowercase' }}>
                        {currency}
                      </span>
                    </strong>
                  </S.WinUpTo>
                )}

                {sponsor && (
                  <Grid
                    container
                    item
                    alignItems="stretch"
                    justify={isDownMd || !sponsorImg ? 'center' : 'flex-start'}
                  >
                    <CustomSponsorIcon
                      color={
                        isDisabledDeal
                          ? theme.colors.grey.light3
                          : theme.colors.paletteFromBO.main
                      }
                    />
                    <S.Sponsor variant={isDownSm ? 'h5' : 'h4'}>
                      {t('challenges.detail.description.sponsor')}
                      &nbsp;
                      {sponsor}
                    </S.Sponsor>
                  </Grid>
                )}
                <S.StyledTeaser
                  // eslint-disable-next-line react/no-danger
                  dangerouslySetInnerHTML={{
                    __html: safeTeaser,
                  }}
                />
                {type === CHALLENGE.DEAL && (
                  <S.ParticipationContainer>
                    <ActionParticipation
                      half={!sponsorImg}
                      isParticipating={isParticipating}
                      currentPhase={currentPhase}
                      hasResultLimit={!!resultLimit}
                      inscriptionsLeft={inscriptionsLeft}
                    />
                  </S.ParticipationContainer>
                )}

                {type !== CHALLENGE.LIST ? (
                  <Grid
                    container
                    item
                    css={
                      !isDownMd && !sponsorImg
                        ? cssContainerCenteredButtons
                        : cssButtonsContainer
                    }
                  >
                    <ParticipateDetailsButtons
                      currentPhase={currentPhase}
                      challenge={challenge}
                      challengeType={type}
                      isDenied={false}
                      isParticipating={isParticipating}
                      hasDetailsButton={false}
                      half
                    />
                  </Grid>
                ) : (
                  <Grid container item justify="center">
                    <ChallengeDeclareButton
                      challenge={challenge}
                      title={getChallengeDescriptionTitle({
                        challenge,
                        hasResults: !!challengeResults,
                        t,
                        language,
                      })}
                    />
                  </Grid>
                )}
              </Grid>
            </Grid>
          </Grid>
        );
    }
  };

  return (
    <S.Card>
      <S.Container container>{getContent()}</S.Container>
    </S.Card>
  );
};

export default ChallengeDescription;
